import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "../../../../redux-store";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UFDoraNumberInput, UFDoraTextInput, UFDoraDimensionsPicker, DoraButton, UFDoraSelect } from "dora-shared/frontend";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faPencilAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import * as yup from "yup";
import { StopUnit } from "../../../../ducks/app/cargo-and-template-shared";
import {
  selectAllStopUnitTemplates,
  selectMyTeamsStopUnitTemplates,
} from "../../../../ducks/data/stop-unit-templates/selectors";

interface StopUnitProps {
  stopUnit: StopUnit;
  onCancelClick?: () => void;
  onDelete?: () => void;
  isNew: boolean;
  canEdit: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  // assignedToDropoffValue: boolean | number; // value or false if not shown
  // consider adding a different AddStopUnit, instead of AddStopUnitTemplate
  onSaveStopUnit: (stopUnit: StopUnit) => void;
}

const transformNanEmptyToNull = (value: any) => {
  return value === "" || isNaN(Number(value)) ? null : value;
};

const stopUnitValidationSchema = yup.object({
  stopUnitTemplateId: yup.string().required(),
  description: yup.string().nullable(),
  quantity: yup
    .number()
    .min(0.01)
    .required()
    .transform(transformNanEmptyToNull),
  weight: yup.number().min(0.01).nullable().transform(transformNanEmptyToNull),
  length: yup.number().min(0.01).nullable().transform(transformNanEmptyToNull),
  width: yup.number().min(0.01).nullable().transform(transformNanEmptyToNull),
  height: yup.number().min(0.01).nullable().transform(transformNanEmptyToNull),
  volume: yup.number().min(0.01).nullable().transform(transformNanEmptyToNull),
  colli: yup.number().min(0.01).nullable().transform(transformNanEmptyToNull),
});

const StopUnitRow = ({
  stopUnit,
  isNew,
  onCancelClick,
  onDelete,
  onEditingChange,
  onSaveStopUnit,
}: StopUnitProps) => {
  const { t } = useTranslation(["components", "settings"]);
  const [editing, setEditing] = useState(isNew);

  useEffect(() => {
    onEditingChange?.(editing);
    return () => onEditingChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const allUnitTemplates = useSelector(selectAllStopUnitTemplates);
  const userSelectableStopUnitTemplates = useSelector(
    selectMyTeamsStopUnitTemplates
  );

  const allUnitTemplatesOptions = useMemo(
    () =>
      allUnitTemplates.map((x) => ({
        value: x.id,
        label: x.name,
      })),
    [allUnitTemplates]
  );

  const userSelectableStopUnitTemplatesOptions = useMemo(
    () =>
      userSelectableStopUnitTemplates.map((x) => ({
        value: x.id,
        label: x.name,
      })),
    [userSelectableStopUnitTemplates]
  );

  const formMethods = useForm<StopUnit>({
    criteriaMode: "all",
    resolver: yupResolver(stopUnitValidationSchema),
    defaultValues: {
      stopUnitTemplateId: stopUnit.stopUnitTemplateId,
      description: stopUnit.description,
      quantity: stopUnit.quantity?.toString() || null,
      weight: stopUnit.weight?.toString() || null,
      length: stopUnit.length?.toString() || null,
      width: stopUnit.width?.toString() || null,
      height: stopUnit.height?.toString() || null,
      volume: stopUnit.volume?.toString() || null,
      colli: stopUnit.colli?.toString() || null,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = formMethods;

  const values = getValues();

  const onCancelEditingClick = () => {
    reset();
    onCancelClick && onCancelClick();
    setEditing(false);
  };

  const onSaveClick = () => {
    const values = getValues();
    const normalizedValues = {
      ...values,
      quantity: transformNanEmptyToNull(values.quantity),
      weight: transformNanEmptyToNull(values.weight),
      length: transformNanEmptyToNull(values.length),
      width: transformNanEmptyToNull(values.width),
      height: transformNanEmptyToNull(values.height),
      volume: transformNanEmptyToNull(values.volume),
      colli: transformNanEmptyToNull(values.colli),
    };
    onSaveStopUnit(normalizedValues);
    setEditing(false);
  };

  // console.log("errors", errors);

  const selectedStopUnitTemplateName = useMemo(
    () =>
      allUnitTemplates.find((x) => x.id === values.stopUnitTemplateId)?.name,
    [values.stopUnitTemplateId, allUnitTemplates]
  );

  return (
    <>
      {editing ? (
        <form
          onSubmit={handleSubmit(onSaveClick)}
          className="stop-unit-line-row"
        >
          <div className="stop-unit-line-row-cell">
            <UFDoraNumberInput
              error={!!errors.quantity}
              register={register("quantity")}
              step={0.01}
              autoFocus={isNew}
            />
          </div>
          <div className="stop-unit-line-row-cell">
            <UFDoraSelect
              selectableOptions={userSelectableStopUnitTemplatesOptions}
              options={allUnitTemplatesOptions}
              name={"stopUnitTemplateId"}
              control={control}
              skipMinWidth
              openOnFocus
              onChange={(templateId: string) => {
                const template = allUnitTemplates.find(
                  (t) => t.id === templateId
                );
                if (template) {
                  setValue("description", template.description ?? "");
                  const currentQuantity = transformNanEmptyToNull(
                    getValues("quantity")
                  );
                  if (currentQuantity === null) {
                    setValue("quantity", template.quantity ?? null);
                  }
                  setValue("weight", template.weight ?? null);
                  setValue("length", template.length ?? null);
                  setValue("width", template.width ?? null);
                  setValue("height", template.height ?? null);
                  setValue("volume", template.volume ?? null);
                  setValue("colli", template.colli ?? null);
                }
              }}
            />
          </div>
          <div className="stop-unit-line-row-cell">
            <UFDoraTextInput register={register("description")} />
          </div>
          <div className="stop-unit-line-row-cell">
            <UFDoraNumberInput
              error={!!errors.colli}
              register={register("colli")}
              step={0.01}
            />
          </div>
          <div className="stop-unit-line-row-cell">
            <UFDoraNumberInput
              error={!!errors.weight}
              register={register("weight")}
              step={0.01}
              // suffix={"kg"}
            />
          </div>
          <div className="stop-unit-line-row-cell">
            <UFDoraDimensionsPicker
              registerLength={register("length")}
              registerWidth={register("width")}
              registerHeight={register("height")}
              error={!!errors.length || !!errors.width || !!errors.height}
              step={0.01}
            />
          </div>
          <div className="stop-unit-line-row-cell">
            <UFDoraNumberInput
              error={!!errors.volume}
              register={register("volume")}
              step={0.01}
              // suffix={
              //   <div>
              //     m<sup>3</sup>
              //   </div>
              // }
            />
          </div>
          <div className="stop-unit-line-row-cell">
            <div className="flex gap-0.5 justify-end">
              <DoraButton
                title={t("components:buttonLabels.cancel")}
                aria-label={t("components:buttonLabels.cancel")}
                variant="ghost"
                type="button"
                onClick={onCancelEditingClick}
                style={{
                  width: "1.9rem",
                  height: "2.5rem",
                  textAlign: "center",
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </DoraButton>
              <DoraButton
                type="submit"
                title={t("components:buttonLabels.save")}
                aria-label={t("components:buttonLabels.save")}
                variant="ghost"
                style={{
                  width: "1.9rem",
                  height: "2.5rem",
                  textAlign: "center",
                }}
              >
                <FontAwesomeIcon
                  style={{
                    color: "var(--success-500)",
                    position: "relative",
                    right: "2px",
                  }}
                  icon={faCheck}
                />
              </DoraButton>
            </div>
          </div>
        </form>
      ) : (
        <div className="stop-unit-line-row">
          <div className="stop-unit-line-row-cell">
            {values.quantity?.toString()}
          </div>
          <div className="stop-unit-line-row-cell">
            {selectedStopUnitTemplateName}
          </div>
          <div className="stop-unit-line-row-cell">{values.description}</div>
          <div className="stop-unit-line-row-cell">
            {values.colli && values.colli.toString()}
          </div>
          <div className="stop-unit-line-row-cell">
            {values.weight && <>{values.weight.toString()}</>}
          </div>
          <div className="stop-unit-line-row-cell">
            {(values.length || values.width || values.height) && (
              <>
                {values.length?.toString() || "-"} x{" "}
                {values.width?.toString() || "-"} x{" "}
                {values.height?.toString() || "-"}
              </>
            )}
          </div>
          <div className="stop-unit-line-row-cell">
            {values.volume && values.volume.toString()}
          </div>
          <div className="stop-unit-line-row-cell">
            <div className="flex gap-1 justify-end">
              <DoraButton variant={"ghost"} onClick={() => setEditing(true)}>
                <FontAwesomeIcon
                  style={{ color: "var(--gray-400)" }}
                  icon={faPencilAlt}
                />
              </DoraButton>
              <DoraButton
                variant={"ghost"}
                onClick={() => onDelete && onDelete()}
              >
                <FontAwesomeIcon
                  style={{ color: "var(--error-500)" }}
                  icon={faTrashAlt}
                />
              </DoraButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StopUnitRow;
